import React, { useState } from 'react';

/**
 * Cashback settings card component
 * Allows users to configure how much cashback they share with referees
 * @param {Object} props
 * @param {Object} props.cashbackSettings - Cashback settings object
 * @param {Object} props.translations - Translation strings
 * @param {function} props.onSave - Callback when settings are saved
 */
const CashbackSettings = ({ cashbackSettings, translations, onSave }) => {
  const t = translations?.cashback_settings || {};
  const [percentage, setPercentage] = useState(cashbackSettings?.referee_cashback_percentage || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [error, setError] = useState(null);

  const maxPercentage = cashbackSettings?.max_referee_cashback_percentage || 10;

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setShowSaved(false);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch('/api/referrals/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          referee_cashback_percentage: percentage
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 3000);
        if (onSave) {
          onSave(data.cashback_settings);
        }
      } else {
        setError(data.errors?.join(', ') || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSliderChange = (e) => {
    setPercentage(parseInt(e.target.value, 10));
    setShowSaved(false);
  };

  return (
    <div className="card border rounded-3" data-testid="cashback-settings">
      <div className="card-body p-4">
        <h3 className="h6 fw-bold mb-2">{t.title || 'Share Cashback with Referees'}</h3>
        <p className="text-muted small mb-4">
          {t.description || 'Give new users a discount when they sign up using your link'}
        </p>

        <div className="mb-3">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">{t.current_label || 'Current Cashback'}</span>
            <span className="fw-semibold text-primary" data-testid="current-cashback">{percentage}%</span>
          </div>
          <div className="d-flex justify-content-between mb-3">
            <span className="text-muted">{t.max_label || 'Maximum Allowed'}</span>
            <span className="fw-semibold" data-testid="max-cashback">{maxPercentage}%</span>
          </div>

          <div className="mb-3">
            <input
              type="range"
              className="form-range"
              min="0"
              max={maxPercentage}
              step="1"
              value={percentage}
              onChange={handleSliderChange}
              data-testid="cashback-slider"
            />
            <div className="d-flex justify-content-between small text-muted">
              <span>0%</span>
              <span>{maxPercentage}%</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            <small>{error}</small>
          </div>
        )}

        <button
          className="btn btn-primary w-100"
          style={{ borderRadius: '8px' }}
          onClick={handleSave}
          disabled={isSaving}
          data-testid="save-cashback-btn"
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              ...
            </>
          ) : showSaved ? (
            <>
              <i className="bi bi-check-circle me-2"></i>
              {t.saved || 'Saved!'}
            </>
          ) : (
            t.save || 'Save'
          )}
        </button>
      </div>
    </div>
  );
};

export default CashbackSettings;
